import { ElementProps } from '@/components/sections/builder/types';
import { ImageWithTextElementDataType } from '@/components/sections/builder/elements/body/image-with-text/types';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { Checkbox } from '@/components/ui/checkbox';
import InputSlider from '@/components/sections/builder/settings/input-slider';

const IMAGE_HEIGHT = [
  { value: 'adapt', label: 'Adapt to image' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageWithTextElementDataType;

  return (
    <div>
      <div className='mb-4 mt-4 font-bold'>Image</div>
      <ImageSetting
        value={data?.image}
        onChange={(v) => builder.setElementData(element.id, { image: v })}
      />
      <div className='mb-2 font-bold md:mt-4'>Mobile Image Height</div>
      <div className='mb-4'>
        <Select
          name='imageHeight'
          value={data.imageHeight}
          onValueChange={(val) => {
            builder.setElementData(element.id, {
              imageHeight: val as string,
            });
          }}
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_HEIGHT.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-2 mt-4 text-xs font-bold'>Desktop image width</div>
      {/* temporary disable */}
      {/* <InputTab
        defaultValue={data.imageSize}
        options={[
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { imageSize: v })}
      /> */}
      <InputSlider
        value={data.imageWidthSlider || 375}
        max={750}
        min={200}
        onChange={(val) => {
          builder.setElementData(element.id, {
            imageWidthSlider: val,
          });
        }}
      />
      <div className='mb-2 mt-4 text-xs font-bold'>Desktop image placement</div>
      <InputTab
        grid={2}
        defaultValue={data.imagePosition}
        options={[
          { label: 'Image first', value: 'left' },
          { label: 'Image second', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { imagePosition: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Desktop content position</div>
      <InputTab
        defaultValue={data.contentPosition}
        options={[
          { label: 'Top', value: 'top' },
          { label: 'Middle', value: 'middle' },
          { label: 'Bottom', value: 'bottom' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentPosition: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Desktop content alignment</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-2 mt-4 text-xs font-bold'>Content layout</div>
      <InputTab
        grid={2}
        defaultValue={data.contentOverlap}
        options={[
          { label: 'No Overlap', value: 'no-overlap' },
          { label: 'Overlap', value: 'overlap' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentOverlap: v })}
      />
      <div className='mb-2 mt-4 font-bold'>Color scheme</div>
      <SchemeSetting
        value={data.sectionColorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            sectionColorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <div className='mb-2 mt-4 font-bold'>Container color scheme</div>
      <SchemeSetting
        value={data.containerColorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            containerColorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <div className='mb-4 mt-4 font-bold'>Animations</div>
      <div className='mb-4 font-bold'>Image behavior</div>
      <div className='mb-4'>
        <Select
          name='backgroundAnimation'
          value={data.backgroundAnimation}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              backgroundAnimation: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='none'>None</SelectItem>
            <SelectItem value='ambient_movement'>Ambient movement</SelectItem>
            <SelectItem value='zoom_on_scroll'>Zoom in scroll</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className='mb-2 mt-4 font-bold'>Mobile layout</div>
      <div className='mb-2 mt-4 text-xs'>Mobile content alignment</div>
      <InputTab
        defaultValue={data.contentAlignMobile}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlignMobile: v })}
      />
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <div className='mb-2 mt-4 text-xs font-bold'>Card Rounded</div>
      <div className='flex gap-x-1'>
        <Checkbox
          id='fullWidth'
          aria-label='Select'
          className='mx-2'
          checked={data.roundedCard}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              roundedCard: !!checked,
            });
          }}
        />
        <label htmlFor='fullWidth'>Show Rounded Card</label>
      </div>
    </div>
  );
}
